import React, { useMemo, useState } from 'react'
import { CSmartTable } from '@coreui/react-pro'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import LoadError from './LoadError'
import { voteKey, withVoteCounts } from './votesUtils'

const SEARCH_FIELDS = [{ key: 'full_name', label: 'Player' }]

// Players with how many of each vote they received and their weighted total. Used by the Team
// and Total tabs; both endpoints return every player in one call, so search and paging are local.
const PlayerVotesTable = ({
  players,
  options,
  loading,
  error,
  onRetry,
  errorMessage,
  noItemsLabel,
  trailing,
}) => {
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [itemsPerPage, setItemsPerPage] = useState(20)

  const rows = useMemo(() => withVoteCounts(players, options), [players, options])

  const items = useMemo(() => {
    const term = searchValue.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((row) =>
      String(row[searchField] ?? '')
        .toLowerCase()
        .includes(term),
    )
  }, [rows, searchField, searchValue])

  const columns = useMemo(
    () => [
      { key: 'full_name', label: 'Player', filter: false },
      ...options.map((option) => ({
        key: voteKey(option),
        label: option.name,
        filter: false,
        _style: { width: '12%' },
        _props: { className: 'text-end' },
      })),
      {
        key: 'total_votes',
        label: 'Total',
        filter: false,
        _style: { width: '12%' },
        _props: { className: 'text-end' },
      },
    ],
    [options],
  )

  const scopedColumns = useMemo(() => {
    const scoped = {
      full_name: (item) => <td className="vt-player">{item.full_name}</td>,
      total_votes: (item) => (
        <td className="text-end">
          <span className="vt-total">{item.total_votes ?? 0}</span>
        </td>
      ),
    }
    options.forEach((option) => {
      const key = voteKey(option)
      scoped[key] = (item) => (
        <td className={`text-end${item[key] ? '' : ' vt-muted'}`}>{item[key]}</td>
      )
    })
    return scoped
  }, [options])

  if (error) return <LoadError message={errorMessage} onRetry={onRetry} />

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={(field) => {
          setSearchField(field)
          setSearchValue('')
        }}
        value={searchValue}
        onSearch={setSearchValue}
        placeholder="Search players..."
        trailing={trailing}
      />

      <div className="ge-datatable ge-datatable--no-select">
        <CSmartTable
          columns={columns}
          items={items}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          noItemsLabel={searchValue.trim() ? 'No players match your search' : noItemsLabel}
          pagination
          columnSorter
          sorterValue={{ column: 'total_votes', state: 'desc' }}
          scopedColumns={scopedColumns}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    </>
  )
}

export default PlayerVotesTable
